"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { useTheme } from "@/components/theme-context";
import { Button } from "@/components/ui-kit/Button";
import { Input } from "@/components/ui-kit/Input";
import {
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  FloatingFocusManager,
  offset,
  flip,
  shift,
} from "@floating-ui/react";
import CheckIcon from "@/icons/svg/Check";
import { cn } from "@/lib/utils";
import ScoreachLogo from "@/icons/svg/ScoreachLogo"
import { usePageTitle } from "@/hooks/use-page-title";
import { THEME_OPTIONS } from "@/lib/theme-options";


/**
 * Login page — email/password authentication via Supabase Auth.
 * Shown to unauthenticated users; middleware redirects here automatically.
 */
export default function LoginPage() {
  usePageTitle("Sign In")
  const router = useRouter();
  const { theme, setTheme } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [themeOpen, setThemeOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: themeOpen,
    onOpenChange: setThemeOpen,
    placement: "bottom-end",
    strategy: "fixed",
    middleware: [offset(6), flip(), shift({ padding: 8 })],
  });
  const themeClick = useClick(context);
  const themeDismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([themeClick, themeDismiss]);

  const currentTheme = THEME_OPTIONS.find((o) => o.value === theme) ?? THEME_OPTIONS[2];
  const ThemeIcon = currentTheme.Icon;

  async function handleSubmit() {
    setError(null);
    setLoading(true);

    const supabase = createClient();
    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }

    // Loading stays true — the component will unmount upon navigation
    router.push("/");
    router.refresh();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      {/* Theme dropdown — top right corner */}
      <div className="fixed right-4 top-4">
        <button
          id="theme-button"
          type="button"
          ref={refs.setReference}
          aria-label={`Appearance: ${currentTheme.label}`}
          aria-expanded={themeOpen}
          aria-haspopup="menu"
          className={cn(
            "flex h-9 w-9 cursor-pointer items-center justify-center rounded-md transition-colors",
            themeOpen
              ? "bg-background-light text-accent"
              : "text-secondary hover:bg-background-light hover:text-accent",
          )}
          {...getReferenceProps()}
        >
          <ThemeIcon className="h-4 w-4" />
        </button>

        {themeOpen && (
          <FloatingFocusManager context={context} modal={false}>
            <div
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="theme-button"
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className="z-50 min-w-36 overflow-hidden rounded-lg border border-border bg-white py-1 shadow-lg"
            >
              {THEME_OPTIONS.map(({ value, label, Icon }) => (
                <button
                  key={value}
                  role="menuitem"
                  type="button"
                  onClick={() => { setTheme(value); setThemeOpen(false); }}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-sm transition-colors",
                    theme === value
                      ? "bg-accent-light font-medium text-primary"
                      : "text-secondary hover:bg-background hover:text-primary",
                  )}
                >
                  <Icon className={cn("h-4 w-4 shrink-0", theme === value ? "text-primary" : "text-secondary")} />
                  {label}
                  {theme === value && <CheckIcon className="ml-auto h-4 w-4 text-accent" />}
                </button>
              ))}
            </div>
          </FloatingFocusManager>
        )}
      </div>

      {/* Card */}
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-sm">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-white">
            <ScoreachLogo className="h-6 w-6" />
          </div>
          <h1 className="text-xl font-semibold text-primary">Scoreach</h1>
          <p className="text-sm text-secondary">Sign in to continue</p>
        </div>

        {/* Form */}
        <form onSubmit={(e) => { e.preventDefault(); void handleSubmit(); }} className="flex flex-col gap-4">
          <Input
            label="Email"
            type="email"
            size="large"
            required
            autoFocus
            autoComplete="email"
            allowAutocomplete
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
          <Input
            label="Password"
            type="password"
            size="large"
            required
            autoComplete="current-password"
            allowAutocomplete
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            state={error ? "error" : "default"}
            errorText={error ?? undefined}
            errorInline
          />
          <Button
            label={loading ? "Signing in…" : "Sign in"}
            loading={loading}
            disabled={!email || !password}
            size="large"
            type="submit"
            className="mt-2 w-full justify-center"
          />
        </form>
      </div>
    </div>
  );
}
